import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { getUser } from "../../utils/auth";
import { getUsers, getLeads, getUserProfile } from "../../services/api";
import {
  getId,
  getReportingManagerId,
  isSalesRole,
} from "../../utils/roleAccess";
import { FiEye } from "react-icons/fi";
import globalStyles from "../../styles/globalStyles";

const PRIMARY_COLOR = "#534AB7";

const s = {
  contentWrapper: { marginTop: -15 },

  row: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20,
    gap: 12,
    flexWrap: "wrap",
  },

  h1: {
    fontSize: 22,
    fontWeight: 700,
    color: "#1e293b",
  },

  btn: {
    padding: "9px 18px",
    background: PRIMARY_COLOR,
    color: "#fff",
    border: "none",
    borderRadius: 8,
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 600,
  },

  profileCard: {
    background: "#fff",
    borderRadius: 10,
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    border: "1px solid #f1f5f9",
    padding: "34px 24px",
    marginBottom: 24,
  },

  avatarWrap: {
    display: "flex",
    justifyContent: "center",
    marginBottom: 34,
  },

  avatar: {
    width: 110,
    height: 110,
    borderRadius: "50%",
    background: "#eaf2ff",
    color: "#7aa2e8",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 42,
    fontWeight: 500,
  },

  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "22px 24px",
  },

  field: {
    width: "100%",
  },

  label: {
    display: "block",
    marginBottom: 5,
    fontSize: 13,
    fontWeight: 500,
    color: "#111827",
  },

  inputLike: {
    width: "100%",
    padding: "10px 12px",
    border: "1px solid #d1d5db",
    borderRadius: 6,
    fontSize: 13,
    color: "#374151",
    background: "#fff",
    minHeight: 38,
    boxSizing: "border-box",
  },

  sectionTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: "#374151",
    marginBottom: 14,
    marginTop: 22,
  },

  table: {
    width: "100%",
    minWidth: 760,
    borderCollapse: "collapse",
    background: "#fff",
    borderRadius: 10,
    overflow: "hidden",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    marginBottom: 24,
  },

  th: {
    background: "#b1b9c1",
    padding: "8px 16px",
    textAlign: "left",
    fontSize: 13,
    fontWeight: 700,
    color: "#374151",
    borderBottom: "1px solid #e2e8f0",
  },

  td: {
    padding: "7px 14px",
    fontSize: 12,
    color: "#374151",
    borderBottom: "1px solid #f1f5f9",
  },

  badge: {
    padding: "3px 10px",
    borderRadius: 99,
    fontSize: 12,
    fontWeight: 600,
    display: "inline-block",
  },

  viewBtn: {
    border: "none",
    background: "#f8fafc",
    color: PRIMARY_COLOR,
    width: 32,
    height: 32,
    borderRadius: 8,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  emptyState: {
    textAlign: "center",
    padding: 34,
    fontSize: 13,
    color: "#64748b",
  },

  loading: {
    fontSize: 14,
    color: "#374151",
  },

  modal: {
    position: "fixed",
    inset: 0,
    padding: 12,
    boxSizing: "border-box",
    background: "rgba(0,0,0,0.4)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 100,
  },

  mcard: {
    background: "#fff",
    padding: 32,
    borderRadius: 12,
    width: 650,
    maxHeight: "90vh",
    overflowY: "auto",
  },

  viewGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: 14,
  },

  viewItem: {
    background: "#f8fafc",
    padding: "10px 12px",
    borderRadius: 8,
    border: "1px solid #e2e8f0",
  },

  viewItemFull: {
    gridColumn: "1 / -1",
    background: "#f8fafc",
    padding: "10px 12px",
    borderRadius: 8,
    border: "1px solid #e2e8f0",
  },

  viewLabel: {
    fontSize: 12,
    color: "#64748b",
    marginBottom: 4,
  },

  viewValue: {
    fontSize: 14,
    color: "#1e293b",
    fontWeight: 500,
    wordBreak: "break-word",
  },

  mrow: {
    display: "flex",
    gap: 10,
    justifyContent: "flex-end",
    marginTop: 14,
  },

  cancel: {
    padding: "9px 16px",
    border: "1px solid #d1d5db",
    background: "#fff",
    borderRadius: 8,
    cursor: "pointer",
    fontSize: 14,
  },
};

const roleColor = {
  admin: "#dbeafe",
  "senior-manager": "#e0e7ff",
  manager: "#d1fae5",
  sales: "#fef9c3",
};

const roleText = {
  admin: "#1d4ed8",
  "senior-manager": "#4338ca",
  manager: "#065f46",
  sales: "#92400e",
};

const getInitials = (name = "") => {
  const words = name.trim().split(" ").filter(Boolean);

  if (words.length >= 2) {
    return `${words[0][0]}${words[1][0]}`.toUpperCase();
  }

  return name.slice(0, 2).toUpperCase() || "NA";
};

const formatLabel = (value) => {
  if (!value) return "—";

  return String(value)
    .replace(/_/g, " ")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const getAssignedToName = (assignedTo, users = []) => {
  if (!assignedTo) return "—";

  if (typeof assignedTo === "object") {
    return assignedTo.name || assignedTo.email || "—";
  }

  const matchedUser = users.find((user) => getId(user) === assignedTo);
  return matchedUser?.name || "—";
};

const getAssignedToId = (assignedTo) => {
  if (!assignedTo) return "";

  if (typeof assignedTo === "object") {
    return getId(assignedTo);
  }

  return String(assignedTo);
};

const useIsMobile = (breakpoint = 768) => {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= breakpoint : false,
  );

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= breakpoint);
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, [breakpoint]);

  return isMobile;
};

const UserTable = ({ title, users, onView }) => (
  <>
    <h2 style={s.sectionTitle}>{title}</h2>

    <div style={globalStyles.tableScroll}>
      <table style={s.table}>
        <thead>
          <tr>
            <th style={s.th}>Name</th>
            <th style={s.th}>Email / Mobile</th>
            <th style={s.th}>Role</th>
            <th style={s.th}>Reporting Manager</th>
            <th style={s.th}>Region</th>
            <th style={s.th}>Certified</th>
            <th style={s.th}>Actions</th>
          </tr>
        </thead>

        <tbody>
          {users.length > 0 ? (
            users.map((user) => (
              <tr key={getId(user)}>
                <td style={s.td}>
                  <strong>{user.name || "—"}</strong>
                  <br />
                  <span style={{ fontSize: 12, color: "#94a3b8" }}>
                    {user.employeeId || "—"}
                  </span>
                </td>

                <td style={s.td}>
                  {user.email || "—"}
                  <br />
                  <span style={{ fontSize: 12, color: "#94a3b8" }}>
                    {user.mobile || ""}
                  </span>
                </td>

                <td style={s.td}>
                  <span
                    style={{
                      ...s.badge,
                      background: roleColor[user.role] || "#f1f5f9",
                      color: roleText[user.role] || "#374151",
                    }}
                  >
                    {user.role || "—"}
                  </span>
                </td>

                <td style={s.td}>
                  {user.reportingManager ? (
                    <>
                      {user.reportingManager.name || "—"}
                      <br />
                      <span style={{ fontSize: 12, color: "#94a3b8" }}>
                        {user.reportingManager.employeeId || ""}
                      </span>
                    </>
                  ) : (
                    "—"
                  )}
                </td>

                <td style={s.td}>{user.region || "—"}</td>

                <td style={s.td}>{user.isCertified ? "✓" : "—"}</td>

                <td style={s.td}>
                  <button
                    type="button"
                    title="View Profile"
                    style={s.viewBtn}
                    onClick={() => onView(user)}
                  >
                    <FiEye size={14} />
                  </button>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="7" style={{ ...s.td, ...s.emptyState }}>
                No records found
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  </>
);

const LeadTable = ({ title, leads, users, onView }) => (
  <>
    <h2 style={s.sectionTitle}>{title}</h2>

    <div style={globalStyles.tableScroll}>
      <table style={s.table}>
        <thead>
          <tr>
            <th style={s.th}>Operator</th>
            <th style={s.th}>Phone</th>
            <th style={s.th}>Business Type</th>
            <th style={s.th}>Stage</th>
            <th style={s.th}>Status</th>
            <th style={s.th}>Actions</th>
          </tr>
        </thead>

        <tbody>
          {leads.length > 0 ? (
            leads.map((lead) => (
              <tr key={getId(lead)}>
                <td style={s.td}>
                  <strong>{lead.operatorId?.name || "—"}</strong>
                </td>

                <td style={s.td}>{lead.operatorId?.phone || "—"}</td>

                <td style={s.td}>{lead.operatorId?.businessType || "—"}</td>

                <td style={s.td}>
                  <span style={globalStyles.stageBadge(lead.currentStage)}>
                    {formatLabel(lead.currentStage)}
                  </span>
                </td>

                <td style={s.td}>
                  <span style={globalStyles.statusBadge(lead.status)}>
                    {formatLabel(lead.status)}
                  </span>
                </td>

                <td style={s.td}>
                  <button
                    type="button"
                    title="View Lead"
                    style={s.viewBtn}
                    onClick={() => onView(lead)}
                  >
                    <FiEye size={14} />
                  </button>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="7" style={{ ...s.td, ...s.emptyState }}>
                No leads found
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  </>
);

export default function UserProfile() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const loggedInUser = getUser() || {};

  const [users, setUsers] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewLead, setViewLead] = useState(null);

  const [profileData, setProfileData] = useState(null);

  const isMobile = useIsMobile();

  const selectedUserFromState = location.state?.user;

  useEffect(() => {
    setLoading(true);

    const profileRequest = id ? getUsers({ limit: 500 }) : getUserProfile();

    Promise.all([
      profileRequest,
      getUsers({ limit: 500 }),
      getLeads({ limit: 500 }),
    ])
      .then(([profileRes, userRes, leadRes]) => {
        const apiUsers =
          userRes?.data?.data?.users ||
          userRes?.data?.users ||
          userRes?.data?.data ||
          [];

        const apiLeads =
          leadRes?.data?.data?.leads ||
          leadRes?.data?.leads ||
          leadRes?.data?.data ||
          [];

        setUsers(Array.isArray(apiUsers) ? apiUsers : []);
        setLeads(Array.isArray(apiLeads) ? apiLeads : []);

        if (!id) {
          const profile =
            profileRes?.data?.data?.profile ||
            profileRes?.data?.profile ||
            null;
          setProfileData(profile);
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [id]);

  const profileUser = useMemo(() => {
    if (!id) {
      return profileData || loggedInUser;
    }

    return (
      selectedUserFromState || users.find((user) => getId(user) === id) || null
    );
  }, [id, profileData, loggedInUser, selectedUserFromState, users]);

  const handleViewUserProfile = (user) => {
    const userId = getId(user);

    if (!userId) return;

    navigate(`/user/${userId}`, {
      state: { user },
    });
  };

  const managersUnderSeniorManager = useMemo(() => {
    if (profileUser?.role !== "senior-manager") return [];

    return users.filter(
      (user) =>
        user.role === "manager" &&
        getReportingManagerId(user) === getId(profileUser),
    );
  }, [users, profileUser]);

  const salesPersonsUnderManager = useMemo(() => {
    if (profileUser?.role !== "manager") return [];

    return users.filter(
      (user) =>
        isSalesRole(user.role) &&
        getReportingManagerId(user) === getId(profileUser),
    );
  }, [users, profileUser]);

  const salesPersonsUnderSeniorManager = useMemo(() => {
    if (profileUser?.role !== "senior-manager") return [];

    const managerIds = managersUnderSeniorManager.map((manager) =>
      getId(manager),
    );

    return users.filter((user) => {
      const reportingManagerId = getReportingManagerId(user);

      return (
        isSalesRole(user.role) &&
        (reportingManagerId === getId(profileUser) ||
          managerIds.includes(reportingManagerId))
      );
    });
  }, [users, profileUser, managersUnderSeniorManager]);

  const leadsUnderSales = useMemo(() => {
    if (profileUser?.role !== "sales") return [];

    const profileUserId = getId(profileUser);

    return leads.filter((lead) => {
      const assignedToId = getAssignedToId(lead.assignedTo);
      return assignedToId === profileUserId;
    });
  }, [leads, profileUser]);

  if (loading) {
    return <div style={s.loading}>Loading profile...</div>;
  }

  if (!profileUser) {
    return <div style={s.loading}>Profile not found.</div>;
  }

  return (
    <div
      style={{
        ...s.contentWrapper,
        ...(isMobile ? { marginTop: 0 } : {}),
      }}
    >
      <div
        style={{
          ...s.row,
          ...(isMobile ? { alignItems: "stretch" } : {}),
        }}
      >
        <h1 style={s.h1}>{profileUser.name || "Profile"}</h1>

        <button
          type="button"
          style={{
            ...s.btn,
            ...(isMobile ? { width: "100%", justifyContent: "center" } : {}),
          }}
          onClick={() => navigate(-1)}
        >
          Back
        </button>
      </div>

      <div
        style={{
          ...s.profileCard,
          ...(isMobile ? { padding: "24px 16px" } : {}),
        }}
      >
        <div style={s.avatarWrap}>
          <div
            style={{
              ...s.avatar,
              ...(isMobile ? { width: 92, height: 92, fontSize: 34 } : {}),
            }}
          >
            {getInitials(profileUser.name)}
          </div>
        </div>

        <div
          style={{
            ...s.grid,
            ...(isMobile ? { gridTemplateColumns: "1fr" } : {}),
          }}
        >
          <div style={s.field}>
            <label style={s.label}>Employee Name :</label>
            <div style={s.inputLike}>{profileUser.name || "—"}</div>
          </div>

          <div style={s.field}>
            <label style={s.label}>Region :</label>
            <div style={s.inputLike}>{profileUser.region || "—"}</div>
          </div>

          <div style={s.field}>
            <label style={s.label}>Role :</label>
            <div style={s.inputLike}>{profileUser.role || "—"}</div>
          </div>

          <div style={s.field}>
            <label style={s.label}>Employee Code :</label>
            <div style={s.inputLike}>{profileUser.employeeId || "—"}</div>
          </div>

          <div style={s.field}>
            <label style={s.label}>Email :</label>
            <div style={s.inputLike}>{profileUser.email || "—"}</div>
          </div>

          <div style={s.field}>
            <label style={s.label}>Mobile :</label>
            <div style={s.inputLike}>{profileUser.mobile || "—"}</div>
          </div>

          <div style={s.field}>
            <label style={s.label}>Reporting Manager :</label>
            <div style={s.inputLike}>
              {profileUser.reportingManager?.name || "—"}
            </div>
          </div>

          <div style={s.field}>
            <label style={s.label}>Certified :</label>
            <div style={s.inputLike}>
              {profileUser.isCertified ? "Yes" : "No"}
            </div>
          </div>
        </div>
      </div>

      {profileUser.role === "senior-manager" && (
        <>
          <UserTable
            title="Managers"
            users={managersUnderSeniorManager}
            onView={handleViewUserProfile}
          />

          <UserTable
            title="Sales Persons"
            users={salesPersonsUnderSeniorManager}
            onView={handleViewUserProfile}
          />
        </>
      )}

      {profileUser.role === "manager" && (
        <UserTable
          title="Sales Persons"
          users={salesPersonsUnderManager}
          onView={handleViewUserProfile}
        />
      )}

      {profileUser.role === "sales" && (
        <LeadTable
          title="Leads Assigned To This Sales Person"
          leads={leadsUnderSales}
          users={users}
          onView={setViewLead}
        />
      )}

      {viewLead && (
        <div style={s.modal}>
          <div
            style={{
              ...s.mcard,
              ...(isMobile
                ? {
                    width: "94vw",
                    maxWidth: "94vw",
                    padding: 18,
                    boxSizing: "border-box",
                  }
                : {}),
            }}
          >
            <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 20 }}>
              View Lead
            </h2>

            <div
              style={{
                ...s.viewGrid,
                ...(isMobile ? { gridTemplateColumns: "1fr" } : {}),
              }}
            >
              <div style={s.viewItem}>
                <div style={s.viewLabel}>Operator</div>
                <div style={s.viewValue}>
                  {viewLead.operatorId?.name || "—"}
                </div>
              </div>

              <div style={s.viewItem}>
                <div style={s.viewLabel}>Phone</div>
                <div style={s.viewValue}>
                  {viewLead.operatorId?.phone || "—"}
                </div>
              </div>

              <div style={s.viewItem}>
                <div style={s.viewLabel}>Business Type</div>
                <div style={s.viewValue}>
                  {viewLead.operatorId?.businessType || "—"}
                </div>
              </div>

              <div style={s.viewItem}>
                <div style={s.viewLabel}>Current Stage</div>
                <div style={s.viewValue}>
                  <span style={globalStyles.stageBadge(viewLead.currentStage)}>
                    {formatLabel(viewLead.currentStage)}
                  </span>
                </div>
              </div>

              <div style={s.viewItem}>
                <div style={s.viewLabel}>Status</div>
                <div style={s.viewValue}>
                  <span style={globalStyles.statusBadge(viewLead.status)}>
                    {formatLabel(viewLead.status)}
                  </span>
                </div>
              </div>

              <div style={s.viewItem}>
                <div style={s.viewLabel}>Assigned To</div>
                <div style={s.viewValue}>
                  {getAssignedToName(viewLead.assignedTo, users)}
                </div>
              </div>

              <div style={s.viewItem}>
                <div style={s.viewLabel}>Closed</div>
                <div style={s.viewValue}>
                  {viewLead.closureFlag ? "Yes" : "No"}
                </div>
              </div>

              <div style={s.viewItem}>
                <div style={s.viewLabel}>Timeline Count</div>
                <div style={s.viewValue}>{viewLead.timeline?.length || 0}</div>
              </div>

              <div style={s.viewItemFull}>
                <div style={s.viewLabel}>Created At</div>
                <div style={s.viewValue}>
                  {viewLead.createdAt
                    ? new Date(viewLead.createdAt).toLocaleString()
                    : "—"}
                </div>
              </div>

              <div style={s.viewItemFull}>
                <div style={s.viewLabel}>Updated At</div>
                <div style={s.viewValue}>
                  {viewLead.updatedAt
                    ? new Date(viewLead.updatedAt).toLocaleString()
                    : "—"}
                </div>
              </div>
            </div>

            <div
              style={{
                ...s.mrow,
                ...(isMobile
                  ? { justifyContent: "stretch", flexWrap: "wrap" }
                  : {}),
              }}
            >
              <button
                type="button"
                style={{
                  ...s.cancel,
                  ...(isMobile ? { width: "100%" } : {}),
                }}
                onClick={() => setViewLead(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
